import { Box, Button, Card, CardContent, Link, Typography } from '@mui/material';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Column, Row, Searchbox, Spacer } from '../../components';
import { MutatorState } from '@aesop-fables/scrinium';
import { useOrgSearch } from '../../hooks/useOrgSearch';
import { useNavigateToOrg } from '../../hooks/useNavigateToOrg';
import { OrgStatus } from '../multitier/AdminDashboard';
import { ToastbarContext } from '../../App';
import { useLoading } from '../../hooks/useLoading';
import { Svgs } from '../../assets/svg';
import { TeamData } from '../../models/TeamData';
import { OrganizationData } from '../../models/OrganizationData';
import { Toast } from '../../models/Toast';
import { TeamMetricsData } from '../../models/MetricsData';
import { OrgSearchResults } from '../multitier/OrgSearchResults';
import { SearchResultCard } from '../multitier/SearchResultCard';

export interface TeamListViewProps {
  teamList: TeamData[];
  allMetrics: TeamMetricsData[];
  select: MutatorState<number>;
  selectGroup?: MutatorState<number>;
  currentGroupId?: number;
  selectOrganization?: MutatorState<number>;
  setOrgStatus?: React.Dispatch<React.SetStateAction<OrgStatus | undefined>>;
  setIsNavigatingToOrg?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TeamListView: React.FC<TeamListViewProps> = ({
  teamList,
  allMetrics,
  select,
  selectGroup,
  currentGroupId,
  selectOrganization,
  setOrgStatus,
  setIsNavigatingToOrg,
}) => {
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();

  const [searchWord, setSearchWord] = useState('');

  const numTeams = teamList?.length ?? 0;
  const trimmedSearch = searchWord.trim();
  const isSearching = trimmedSearch.length > 0;

  const { orgResults, orgSearchLoading } = useOrgSearch({
    setToast,
    trimmedSearch,
    isSearching,
  });

  const filteredTeams = useMemo(() => {
    if (!isSearching) return teamList;
    const lowerQuery = trimmedSearch.toLowerCase();
    return teamList.filter(team => team.name?.toLowerCase().includes(lowerQuery));
  }, [teamList, isSearching, trimmedSearch]);

  const selectTeam = async (teamId: number) => {
    await select.action(teamId);
  };

  const onViewTeam = async (teamId: number | undefined) => {
    if (teamId === undefined) {
      setToast(new Toast({ message: 'No team id found', severity: 'error', open: true }));
      return;
    }
    setLoading(true);
    selectTeam(teamId);
  };

  const selectParents = useCallback(
    async (org: OrganizationData) => {
      const groupId = org.teamOrgMap?.team?.group?.id;
      const teamId = org.teamOrgMap?.team?.id;
      if (groupId !== undefined && groupId !== currentGroupId) {
        await selectGroup?.action(groupId);
      }
      if (teamId !== undefined) {
        await select.action(teamId);
      }
    },
    [selectGroup, select, currentGroupId],
  );

  const onViewOrg = useNavigateToOrg({
    selectOrganization,
    setOrgStatus,
    setIsNavigatingToOrg,
    selectParents,
  });

  const getOrgSubtitle = (org: OrganizationData) => {
    const teamName = org.teamOrgMap?.team?.name;
    return teamName ? `Organization \u2022 ${teamName}` : 'Organization';
  };

  const hasTeamResults = filteredTeams.length > 0;
  const hasOrgResults = orgResults.length > 0;
  const noResults = isSearching && !hasTeamResults && !hasOrgResults && !orgSearchLoading;

  return (
    <>
      <Spacer height='xs' />
      <Card variant='ghost' color='primary'>
        <CardContent className='searchbox'>
          <Column>
            <Searchbox
              callback={value => setSearchWord(value)}
              label='Search'
              placeholder='Search'
            />
          </Column>
        </CardContent>
      </Card>

      <Spacer height='xs' />
      <Card variant='ghost' color='primary'>
        <CardContent className='pad25'>
          {numTeams === 0 && !isSearching ? (
            <Typography
              variant='p20'
              color='secondary.main'
              textAlign='center'
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              You haven't created any teams yet.
            </Typography>
          ) : isSearching ? (
            <>
              {hasTeamResults && (
                <>
                  <Typography variant='p16Light' color='secondary.dark' sx={{ lineHeight: 1 }}>
                    Teams
                  </Typography>
                  <Spacer height='xxs' />
                  {filteredTeams.map((team, i) => {
                    const metrics = allMetrics.find(x => x.teamId === team.id);
                    const metaDetail = [
                      metrics?.organizations !== undefined &&
                        `${metrics.organizations} Organizations`,
                      metrics?.activeLicenses !== undefined &&
                        `${metrics.activeLicenses} Active Users`,
                    ]
                      .filter(Boolean)
                      .join(' | ');
                    const metaString = metaDetail ? `Team \u2022 ${metaDetail}` : 'Team';

                    return (
                      <SearchResultCard
                        key={team.id}
                        icon={<Svgs.IconTeamsDefault />}
                        name={team.name ?? ''}
                        subtitle={metaString}
                        onView={() => onViewTeam(team.id)}
                        isFirst={i === 0}
                      />
                    );
                  })}
                </>
              )}

              {hasTeamResults && (hasOrgResults || orgSearchLoading) && <Spacer height='sm' />}

              <OrgSearchResults
                orgResults={orgResults}
                orgSearchLoading={orgSearchLoading}
                onViewOrg={onViewOrg}
                getSubtitle={getOrgSubtitle}
              />

              {noResults && (
                <Row style={{ justifyContent: 'center', padding: '5.5px 0' }}>
                  <Typography variant='p18SemiBold' color='secondary.main'>
                    No results found
                  </Typography>
                </Row>
              )}
            </>
          ) : (
            filteredTeams.map((team, i) => {
              const metrics = allMetrics.find(x => x.teamId === team.id);
              return (
                <Card
                  variant='ghost'
                  color='secondary'
                  key={team.id}
                  sx={{ marginTop: i === 0 ? '0px' : '10px' }}>
                  <CardContent sx={{ padding: '13px 20px !important' }}>
                    <Row style={{ justifyContent: 'space-between' }}>
                      <Row
                        style={{
                          alignItems: 'center',
                        }}>
                        <Svgs.IconTeamsDefault />
                      </Row>
                      <Row
                        style={{
                          alignItems: 'center',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                        <Spacer width='xxs' />
                        <Link
                          className='darkenOnHover'
                          noWrap={true}
                          sx={{ flex: 1 }}
                          variant='p18SemiBold'
                          color='secondary.main'
                          onClick={() => {
                            onViewTeam(team.id);
                          }}>
                          {team.name}
                        </Link>
                        <Box sx={{ width: '15px' }} />
                      </Row>

                      <Row
                        style={{
                          alignItems: 'center',
                        }}>
                        {metrics ? (
                          <>
                            <Column>
                              <Typography variant='p12SemiBold' color='secondary.main'>
                                Organizations
                              </Typography>
                              <Row style={{ alignItems: 'center' }}>
                                <Svgs.IconOrgPaysLarge />
                                <Spacer width='xxs' />
                                <Typography variant='p20SemiBold' color='secondary.main'>
                                  {metrics.organizations}
                                </Typography>
                              </Row>
                            </Column>
                            <Box sx={{ width: { xs: '15px', lg: '40px' } }} />
                            <Column>
                              <Typography variant='p12SemiBold' color='secondary.main'>
                                Active users
                              </Typography>
                              <Row style={{ alignItems: 'center' }}>
                                <Svgs.IconMembersDefault />
                                <Spacer width='xxs' />
                                <Typography variant='p20SemiBold' color='secondary.main'>
                                  {metrics.activeLicenses}
                                </Typography>
                              </Row>
                            </Column>
                          </>
                        ) : (
                          <div style={{ padding: '24px ' }} />
                        )}

                        <Box
                          sx={{
                            width: { xs: '15px', md: '20px', lg: '40px' },
                          }}
                        />
                        <Button
                          onClick={() => onViewTeam(team.id)}
                          className='cardAction'
                          color='secondary'>
                          View
                        </Button>
                      </Row>
                    </Row>
                  </CardContent>
                </Card>
              );
            })
          )}
        </CardContent>
      </Card>
    </>
  );
};
